import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
  Slider,
  InputAdornment
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  TrendingUp,
  Assessment,
  Info,
  Save,
  Cancel,
  Refresh
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import Layout from '../components/Layout/Layout';
import { apiService } from '../services/api';

interface CustomKPI {
  id?: string;
  name: string;
  description: string;
  type: string;
  dataSource: string;
  aggregation: string;
  formula: string;
  targetValue?: number;
  thresholds?: {
    excellent?: number;
    good?: number;
    average?: number;
    poor?: number;
    critical?: number;
  };
  unit: string;
  displayConfig?: {
    decimals?: number;
    prefix?: string;
    suffix?: string;
    chartType?: string;
    showTrend?: boolean;
  };
  isActive: boolean;
}

const CustomKPIBuilder: React.FC = () => {
  const [kpis, setKpis] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKPI, setEditingKPI] = useState<CustomKPI | null>(null);
  const [kpiResults, setKpiResults] = useState<any[]>([]);
  
  // Form state
  const [formData, setFormData] = useState<CustomKPI>({
    name: '',
    description: '',
    type: 'velocity',
    dataSource: 'sprints',
    aggregation: 'average',
    formula: '',
    targetValue: undefined,
    thresholds: {
      excellent: undefined,
      good: undefined,
      average: undefined,
      poor: undefined,
      critical: undefined
    },
    unit: '',
    displayConfig: {
      decimals: 1,
      chartType: 'line',
      showTrend: true
    },
    isActive: true
  });

  useEffect(() => {
    loadKPIs();
  }, []);

  const loadKPIs = async () => {
    try {
      setLoading(true);
      const data = await apiService.getCustomKPIs();
      setKpis(data);
      
      // Calculate all KPIs
      const results = await apiService.calculateAllKPIs();
      setKpiResults(results);
    } catch (error) {
      console.error('Error loading KPIs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (kpi?: any) => {
    if (kpi) {
      setEditingKPI(kpi);
      setFormData(kpi);
    } else {
      setEditingKPI(null);
      setFormData({
        name: '',
        description: '',
        type: 'velocity',
        dataSource: 'sprints',
        aggregation: 'average',
        formula: '',
        unit: '',
        displayConfig: {
          decimals: 1,
          chartType: 'line',
          showTrend: true
        },
        isActive: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingKPI(null);
  };

  const handleSaveKPI = async () => {
    try {
      if (editingKPI?.id) {
        await apiService.updateCustomKPI(editingKPI.id, formData);
      } else {
        await apiService.createCustomKPI(formData);
      }
      handleCloseDialog();
      loadKPIs();
    } catch (error: any) {
      alert('Error saving KPI: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteKPI = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this KPI?')) {
      try {
        await apiService.deleteCustomKPI(id);
        loadKPIs();
      } catch (error: any) {
        alert('Error deleting KPI: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleInitializeSystemKPIs = async () => {
    try {
      await apiService.initializeSystemKPIs();
      loadKPIs();
    } catch (error: any) {
      alert('Error initializing KPIs: ' + (error.response?.data?.message || error.message));
    }
  };

  const getKPIResult = (kpiId: string) => {
    return kpiResults.find(r => r.kpiId === kpiId);
  };

  const getThresholdColor = (threshold: string) => {
    switch (threshold) {
      case 'excellent': return 'success';
      case 'good': return 'info';
      case 'average': return 'warning';
      case 'poor': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  return (
    <Layout>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Custom KPI Builder
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadKPIs}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Assessment />}
              onClick={handleInitializeSystemKPIs}
            >
              Initialize System KPIs
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Create KPI
            </Button>
          </Box>
        </Box>

        {/* KPI Cards Grid */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {kpis.map((kpi) => {
            const result = getKPIResult(kpi.id);
            return (
              <Grid item xs={12} sm={6} md={4} key={kpi.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6">{kpi.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {kpi.description}
                        </Typography>
                      </Box>
                      <Box>
                        {!kpi.isSystemKPI && (
                          <>
                            <IconButton size="small" onClick={() => handleOpenDialog(kpi)}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteKPI(kpi.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>

                    {result && (
                      <Box>
                        <Typography variant="h3" sx={{ mb: 1 }}>
                          {result.formattedValue}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <Chip 
                            label={result.threshold} 
                            color={getThresholdColor(result.threshold)}
                            size="small"
                          />
                          {kpi.isSystemKPI && (
                            <Chip label="System" size="small" variant="outlined" />
                          )}
                        </Box>

                        {result.targetValue && (
                          <Typography variant="body2" color="textSecondary">
                            Target: {result.targetValue} {result.unit}
                            {result.targetAchieved && ' ✓'}
                          </Typography>
                        )}

                        {result.trend && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                            <TrendingUp 
                              fontSize="small"
                              sx={{ 
                                transform: result.trend.direction === 'down' ? 'rotate(180deg)' : 'none',
                                color: result.trend.direction === 'up' ? 'success.main' : 'error.main'
                              }}
                            />
                            <Typography variant="caption">
                              {result.trend.percentage.toFixed(1)}% vs. last period
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* KPI Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              All KPIs
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Data Source</TableCell>
                    <TableCell>Current Value</TableCell>
                    <TableCell>Target</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kpis.map((kpi) => {
                    const result = getKPIResult(kpi.id);
                    return (
                      <TableRow key={kpi.id}>
                        <TableCell>
                          {kpi.name}
                          {kpi.isSystemKPI && (
                            <Chip label="System" size="small" variant="outlined" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell>{kpi.type}</TableCell>
                        <TableCell>{kpi.dataSource}</TableCell>
                        <TableCell>{result?.formattedValue || 'N/A'}</TableCell>
                        <TableCell>{kpi.targetValue || 'N/A'}</TableCell>
                        <TableCell>
                          {result && (
                            <Chip 
                              label={result.threshold} 
                              color={getThresholdColor(result.threshold)}
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {!kpi.isSystemKPI && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton size="small" onClick={() => handleOpenDialog(kpi)}>
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDeleteKPI(kpi.id)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Create/Edit KPI Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingKPI ? 'Edit KPI' : 'Create New KPI'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="KPI Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    label="Type"
                  >
                    <MenuItem value="velocity">Velocity</MenuItem>
                    <MenuItem value="quality">Quality</MenuItem>
                    <MenuItem value="delivery">Delivery</MenuItem>
                    <MenuItem value="efficiency">Efficiency</MenuItem>
                    <MenuItem value="utilization">Utilization</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Data Source</InputLabel>
                  <Select
                    value={formData.dataSource}
                    onChange={(e) => setFormData({ ...formData, dataSource: e.target.value })}
                    label="Data Source"
                  >
                    <MenuItem value="issues">Issues</MenuItem>
                    <MenuItem value="sprints">Sprints</MenuItem>
                    <MenuItem value="projects">Projects</MenuItem>
                    <MenuItem value="users">Users</MenuItem>
                    <MenuItem value="time_logs">Time Logs</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Aggregation</InputLabel>
                  <Select
                    value={formData.aggregation}
                    onChange={(e) => setFormData({ ...formData, aggregation: e.target.value })}
                    label="Aggregation"
                  >
                    <MenuItem value="sum">Sum</MenuItem>
                    <MenuItem value="average">Average</MenuItem>
                    <MenuItem value="count">Count</MenuItem>
                    <MenuItem value="percentage">Percentage</MenuItem>
                    <MenuItem value="ratio">Ratio</MenuItem>
                    <MenuItem value="min">Minimum</MenuItem>
                    <MenuItem value="max">Maximum</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Formula"
                  value={formData.formula}
                  onChange={(e) => setFormData({ ...formData, formula: e.target.value })}
                  placeholder="e.g., SUM(completedStoryPoints) / COUNT(sprints)"
                  helperText="Enter calculation formula. Use field names from the selected data source."
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Target Value"
                  type="number"
                  value={formData.targetValue || ''}
                  onChange={(e) => setFormData({ ...formData, targetValue: parseFloat(e.target.value) })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">Goal:</InputAdornment>
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Unit"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  placeholder="e.g., %, SP, hours, count"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Thresholds (for color coding)
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <TextField
                      fullWidth
                      label="Excellent"
                      type="number"
                      size="small"
                      value={formData.thresholds?.excellent || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        thresholds: { ...formData.thresholds, excellent: parseFloat(e.target.value) }
                      })}
                      InputProps={{
                        endAdornment: <InputAdornment position="end"><Box sx={{ width: 16, height: 16, bgcolor: 'success.main', borderRadius: '50%' }} /></InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <TextField
                      fullWidth
                      label="Good"
                      type="number"
                      size="small"
                      value={formData.thresholds?.good || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        thresholds: { ...formData.thresholds, good: parseFloat(e.target.value) }
                      })}
                      InputProps={{
                        endAdornment: <InputAdornment position="end"><Box sx={{ width: 16, height: 16, bgcolor: 'info.main', borderRadius: '50%' }} /></InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <TextField
                      fullWidth
                      label="Average"
                      type="number"
                      size="small"
                      value={formData.thresholds?.average || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        thresholds: { ...formData.thresholds, average: parseFloat(e.target.value) }
                      })}
                      InputProps={{
                        endAdornment: <InputAdornment position="end"><Box sx={{ width: 16, height: 16, bgcolor: 'warning.light', borderRadius: '50%' }} /></InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <TextField
                      fullWidth
                      label="Poor"
                      type="number"
                      size="small"
                      value={formData.thresholds?.poor || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        thresholds: { ...formData.thresholds, poor: parseFloat(e.target.value) }
                      })}
                      InputProps={{
                        endAdornment: <InputAdornment position="end"><Box sx={{ width: 16, height: 16, bgcolor: 'warning.main', borderRadius: '50%' }} /></InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <TextField
                      fullWidth
                      label="Critical"
                      type="number"
                      size="small"
                      value={formData.thresholds?.critical || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        thresholds: { ...formData.thresholds, critical: parseFloat(e.target.value) }
                      })}
                      InputProps={{
                        endAdornment: <InputAdornment position="end"><Box sx={{ width: 16, height: 16, bgcolor: 'error.main', borderRadius: '50%' }} /></InputAdornment>
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Decimal Places"
                  type="number"
                  size="small"
                  value={formData.displayConfig?.decimals || 1}
                  onChange={(e) => setFormData({
                    ...formData,
                    displayConfig: { ...formData.displayConfig, decimals: parseInt(e.target.value) }
                  })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Chart Type</InputLabel>
                  <Select
                    value={formData.displayConfig?.chartType || 'line'}
                    onChange={(e) => setFormData({
                      ...formData,
                      displayConfig: { ...formData.displayConfig, chartType: e.target.value }
                    })}
                    label="Chart Type"
                  >
                    <MenuItem value="line">Line Chart</MenuItem>
                    <MenuItem value="bar">Bar Chart</MenuItem>
                    <MenuItem value="area">Area Chart</MenuItem>
                    <MenuItem value="gauge">Gauge</MenuItem>
                    <MenuItem value="number">Number Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.displayConfig?.showTrend || false}
                      onChange={(e) => setFormData({
                        ...formData,
                        displayConfig: { ...formData.displayConfig, showTrend: e.target.checked }
                      })}
                    />
                  }
                  label="Show Trend Indicator"
                />
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info" icon={<Info />}>
                  <Typography variant="caption">
                    <strong>Formula Examples:</strong><br/>
                    • SUM(storyPoints) - Total story points<br/>
                    • COUNT(issues) / COUNT(sprints) - Issues per sprint<br/>
                    • AVG(velocity) - Average velocity<br/>
                    • SUM(completedStoryPoints) / SUM(plannedStoryPoints) * 100 - Completion percentage
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} startIcon={<Cancel />}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveKPI} 
              variant="contained" 
              startIcon={<Save />}
              disabled={!formData.name || !formData.formula}
            >
              Save KPI
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default CustomKPIBuilder;

